import { debounce } from "lodash";
import * as React from "react";
import { Channel, Housing, PriceClient, PriceResponse } from "../api/ApiClient";
import { CopyOverride } from "../api/Config";
import PriceDescriptionModal from "./PriceDescriptionModal";
declare global {
  interface Window {
    ClientCopyOverride: CopyOverride;
  }
}

interface Props {
  onChangeConsumption?(consumption: number, housing: Housing): void;
  initHousing: Housing;
  initArea: number;
  initConsumption: number;
}

interface State {
  housing: Housing;
  area: number;
  consumption: number;
  price?: PriceResponse;
  calculating: boolean;
  showConsumption: boolean;
}

export default class PriceCalculator extends React.Component<Props, State> {
  private priceClient: PriceClient = new PriceClient();

  constructor(props: Props) {
    super(props);

    this.getPrice = debounce(this.getPrice, 200);

    this.state = {
      housing: this.props.initHousing || Housing.LAGENHET,
      area: this.props.initArea || 60,
      consumption: this.props.initConsumption || 1980,
      calculating: false,
      showConsumption: false,
    };
  }

  componentDidMount() {
    this.getPrice(this.state.consumption);
  }

  render() {
    return (
      <div className="price-calculator">
        <div className="price-calculator-houses">
          <div
            className={
              "price-calculator-house" +
              (this.state.housing == Housing.LAGENHET ? " selected" : "")
            }
            onClick={() => this.onChangeHousing(Housing.LAGENHET)}>
            <div className="price-calculator-house-1"></div>
            <span>Lägenhet</span>
          </div>
          <div
            className={
              "price-calculator-house" +
              (this.state.housing == Housing.RADHUS ? " selected" : "")
            }
            onClick={() => this.onChangeHousing(Housing.RADHUS)}>
            <div className="price-calculator-house-2"></div>
            <span>Radhus</span>
          </div>
          <div
            className={
              "price-calculator-house" +
              (this.state.housing == Housing.VILLA ? " selected" : "")
            }
            onClick={() => this.onChangeHousing(Housing.VILLA)}>
            <div className="price-calculator-house-3"></div>
            <span>Villa</span>
          </div>
        </div>
        <div className="form-group">
          <label className="control-label mb-0">Hur stort bor du?</label>
          <p>{window.ClientCopyOverride?.SignupSliderHelp ?? "Dra i fågeln"}</p>
          <input
            onChange={this.updateArea}
            value={this.state.area}
            type="range"
            min="30"
            max="400"
            step="10"
          />
        </div>
        <div className="form-group">
          <div className="input-group">
            <input
              value={this.state.area}
              name="area"
              placeholder={"60"}
              type="number"
              className={"form-control"}
              onChange={this.updateArea}
              step="10"
              min="0"
              id="input-area"
            />
            <div className="input-group-append">
              <label className="input-group-text" htmlFor="input-area">
                kvadratmeter
              </label>
            </div>
          </div>
        </div>
        {this.state.showConsumption && (
          <div className="form-group">
            <div className="input-group">
              <input
                value={this.state.consumption}
                name="consumtion"
                type="number"
                step={100}
                min={0}
                max={50000}
                className="form-control"
                onChange={this.updateConsumption}
              />
              <div className="input-group-append">
                <span className="input-group-text">kWh</span>
              </div>
            </div>
          </div>
        )}
        <div
          className={
            "textField color-transition" +
            (this.state.calculating ? " color-lightgrey" : "")
          }>
          ca {this.state.price && this.state.price.month} kr / månad
        </div>
        <div className="row">
          <div className="col text-center">
            <a href="#prisModal" data-toggle="modal">
              Så här räknar vi
            </a>
          </div>
        </div>
        <PriceDescriptionModal
          price={this.state.price}
          consumption={this.state.consumption}
          onChangeConsumption={this.onChangeConsumption}
          channel={(window as any).channel}
        />
      </div>
    );
  }

  onChangeHousing = (housing: Housing): void => {
    let area =
      housing == Housing.LAGENHET ? 60 : housing == Housing.RADHUS ? 90 : 110;
    let consumption = this.getConsumption(housing, area);

    this.setState({
      housing,
      area,
      consumption,
    });

    this.props.onChangeConsumption &&
      this.props.onChangeConsumption(consumption, housing);
    this.getPrice(consumption);
  };

  onChangeArea = (area: number): void => {
    let consumption = this.getConsumption(this.state.housing, area);

    this.setState({
      area,
      consumption,
    });

    this.props.onChangeConsumption &&
      this.props.onChangeConsumption(consumption, this.state.housing);
    this.getPrice(consumption);
  };

  updateArea = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.onChangeArea(parseInt(event.currentTarget.value));
  };

  updateConsumption = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.onChangeConsumption(parseInt(event.target.value));
  };

  onChangeConsumption = (consumption: number): void => {
    this.setState({
      consumption,
      showConsumption: true,
    });

    this.props.onChangeConsumption &&
      this.props.onChangeConsumption(consumption, this.state.housing);
    this.getPrice(consumption);
  };

  getConsumption = (housing: Housing, area: number): number => {
    let startConsumption =
      housing == Housing.VILLA ? 6000 : housing == Housing.RADHUS ? 3000 : 0;
    let multiple = housing == Housing.VILLA ? 64 : 33;
    return startConsumption + multiple * area;
  };

  getPrice = async (consumption: number): Promise<void> => {
    this.setState({ calculating: true });
    try {
      let price = await this.priceClient.calculate(
        consumption,
        (window as any).channel
      );
      this.setState({ price, calculating: false });
    } catch (error) {
      console.error("Failed to calculate price", error);
      this.setState({ price: null, calculating: false });
    }
  };

  getState = (): State => {
    return this.state;
  };

  setArea = (area: number) => {
    this.onChangeArea(area);
  };
}
